import type { useAuth } from "@/hooks/auth";
import type { IClient } from "@/models/clients";
import type {
  FeatureAccessContext,
  FeatureAccessRow,
} from "@/models/featureAccess";

type Auth = ReturnType<typeof useAuth>;

type BuildFeatureAccessRowsParams = {
  auth: Auth;
  client: IClient | null;
  context: FeatureAccessContext;
  roleIsAnalyst: boolean;
};

export const buildFeatureAccessRows = ({
  auth,
  client,
  context,
  roleIsAnalyst,
}: BuildFeatureAccessRowsParams) => {
  const hasActiveClient = !!client?.id;
  const isClientSettings = context === "clientSettings";
  const adminOrClientAdmin = auth.isAdmin || auth.isClientAdmin;
  const adminOrSuperAdmin = auth.isAdmin || auth.isSuperAdmin;

  const rows: FeatureAccessRow[] = [
    {
      key: "attributeBuilder",
      titleI18n: "ORGANIZATION.ENABLE_ATTRIBUTE_BUILDER",
      subtitleI18n: "ORGANIZATION.ENABLE_ATTRIBUTE_BUILDER_SUBTITLE",
      field: "can_manage_attribute_builder",
      visible: auth.isLendflowUser,
      dataCy: "feature-access-row-attribute-builder",
    },
    {
      key: "borrowerPlatform",
      titleI18n: "ORGANIZATION.ENABLE_BORROWER_PLATFORM",
      subtitleI18n: "ORGANIZATION.ENABLE_BORROWER_PLATFORM_SUBTITLE",
      field: "is_redirect_to_borrower_platform_enabled",
      visible: isClientSettings ? hasActiveClient : true,
      dataCy: "feature-access-row-borrower-platform",
      afterSaveAction: "auth/getAuthClientSettings",
    },
    {
      key: "byoc",
      titleI18n: "ORGANIZATION.BYOC.ENABLE",
      subtitleI18n: "ORGANIZATION.BYOC.ENABLE_SUBTITLE",
      field: "can_manage_client_credentials",
      visible: adminOrSuperAdmin,
      dataCy: "feature-access-row-byoc",
    },
    {
      key: "contractBuilder",
      titleI18n: "ORGANIZATION.ENABLE_CONTRACT_BUILDER",
      subtitleI18n: "ORGANIZATION.ENABLE_CONTRACT_BUILDER_SUBTITLE",
      field: "can_manage_docusign",
      visible: isClientSettings ? adminOrClientAdmin : auth.isAdmin,
      dataCy: "feature-access-row-contract-builder",
    },
    {
      key: "dataOrchestration",
      titleI18n: "ORGANIZATION.ENABLE_DATA_ORCHESTRATION",
      subtitleI18n: "ORGANIZATION.ENABLE_DATA_ORCHESTRATION_SUBTITLE",
      field: "can_manage_data_orchestration",
      visible: isClientSettings
        ? auth.isAdmin && hasActiveClient
        : !roleIsAnalyst && auth.isAdmin && hasActiveClient,
      dataCy: "feature-access-row-data-orchestration",
    },
    {
      key: "emailTemplates",
      titleI18n: "ORGANIZATION.ENABLE_EMAIL_TEMPLATES_BUILDER",
      subtitleI18n: "ORGANIZATION.ENABLE_EMAIL_TEMPLATES_SUBTITLE",
      field: "has_custom_emails_enabled",
      visible: isClientSettings ? adminOrClientAdmin : auth.isAdmin,
      dataCy: "feature-access-row-email-templates",
    },
    {
      key: "scorecards",
      titleI18n: "ORGANIZATION.ENABLE_SCORECARDS",
      subtitleI18n: "ORGANIZATION.ENABLE_SCORECARDS_SUBTITLE",
      field: "can_manage_score_cards",
      visible: adminOrSuperAdmin,
      dataCy: "feature-access-row-scorecards",
    },
    {
      key: "smsTemplates",
      titleI18n: "ORGANIZATION.ENABLE_SMS_TEMPLATES",
      subtitleI18n: "ORGANIZATION.ENABLE_SMS_TEMPLATES_SUBTITLE",
      field: "can_manage_sms_templates",
      visible: isClientSettings ? adminOrClientAdmin : auth.isAdmin,
      dataCy: "feature-access-row-sms-templates",
    },
    {
      key: "underwriting",
      titleI18n: "ORGANIZATION.ENABLE_UNDERWRITING",
      subtitleI18n: "ORGANIZATION.ENABLE_UNDERWRITING_SUBTITLE",
      field: "can_create_underwriter",
      visible: auth.isAdmin && hasActiveClient,
      dataCy: "feature-access-row-underwriting",
    },
    {
      key: "workflowBuilder",
      titleI18n: "ORGANIZATION.ENABLE_WORKFLOW_BUILDER",
      subtitleI18n: "ORGANIZATION.ENABLE_WORKFLOW_BUILDER_SUBTITLE",
      field: "can_manage_workflow_builder",
      visible: adminOrSuperAdmin,
      dataCy: "feature-access-row-workflow-builder",
    },
  ];

  return rows;
};
